import {createContext, useCallback, useEffect, useReducer} from 'react';
import PropTypes from 'prop-types';
import {authApi} from 'src/api/auth';
import {Issuer} from 'src/utils/auth';
import {paths} from "src/paths";
import Swal from "sweetalert2";

const STORAGE_KEY = 'accessToken';

var ActionType;
(function (ActionType) {
    ActionType['INITIALIZE'] = 'INITIALIZE';
    ActionType['SIGN_IN'] = 'SIGN_IN';
    ActionType['SIGN_OUT'] = 'SIGN_OUT';
})(ActionType || (ActionType = {}));

const initialState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null
};

const handlers = {
    INITIALIZE: (state, action) => {
        const {isAuthenticated, user} = action.payload;

        return {
            ...state,
            isAuthenticated,
            isInitialized: true,
            user
        };
    },
    SIGN_IN: (state, action) => {
        const {user} = action.payload;

        return {
            ...state,
            isAuthenticated: true,
            user
        };
    },
    SIGN_UP: (state, action) => {
        const {user} = action.payload;

        return {
            ...state,
            isAuthenticated: true,
            user
        };
    },
    SIGN_OUT: (state) => ({
        ...state,
        isAuthenticated: false,
        user: null
    })
};

const reducer = (state, action) => (handlers[action.type]
    ? handlers[action.type](state, action)
    : state);

export const AuthContext = createContext({
    ...initialState,
    issuer: Issuer.appAuth,
    signIn: () => Promise.resolve(),
    changePassword: () => Promise.resolve(),
    signOut: () => Promise.resolve(),
});

export const AuthProvider = (props) => {
    const {children} = props;
    const [state, dispatch] = useReducer(reducer, initialState);
    const getToken = () => {
        try {
            let token_data = JSON.parse(localStorage.getItem("token"));
            if (token_data && token_data.access_token && token_data.token_type) {
                return {headers: {Authorization: `${token_data.token_type} ${token_data.access_token}`}}
            } else {
                return null
            }
        } catch (e) {
            return console.error(e)
        }


    }
    const getProfile = () => {
        let user = JSON.parse(localStorage.getItem("user"))
        if (user) {
            return user
        } else {
            return null
        }
    }
    const getPermission = () => {
        try {
            let user = JSON.parse(localStorage.getItem("user"))
            if (user) {
                return user.permission ?? []
            } else {
                return []
            }
        } catch (e) {
            return console.error('permission error', e)
        }

    }
    const initialize = useCallback(async () => {
        try {
            const accessToken = window.localStorage.getItem(STORAGE_KEY);

            if (accessToken) {
                const user = await authApi.me({accessToken});

                dispatch({
                    type: ActionType.INITIALIZE,
                    payload: {
                        isAuthenticated: true,
                        user
                    }
                });
            } else {
                dispatch({
                    type: ActionType.INITIALIZE,
                    payload: {
                        isAuthenticated: false,
                        user: null
                    }
                });
            }
        } catch (err) {
            console.error(err);
            dispatch({
                type: ActionType.INITIALIZE,
                payload: {
                    isAuthenticated: false,
                    user: null
                }
            });
        }
    }, [dispatch]);

    useEffect(() => {
            initialize();
        },
        // eslint-disable-next-line
        []);

    const signIn = useCallback(async (request) => {
        let {username, password, FCMToken} = request;
        return new Promise(async (resolve, reject) => {
            try {
                const {access_token} = await authApi.signIn({username, password, FCMToken});
                const user = await authApi.me({access_token});
                console.log("access_token", access_token)
                localStorage.setItem(STORAGE_KEY, access_token);
                dispatch({
                    type: ActionType.SIGN_IN,
                    payload: {
                        user
                    }
                });
                resolve({access_token});

            } catch (err) {
                console.error('[Auth Api]: ', err);
                reject(new Error('Can not login, Please check your Username or password'));
            }
        })

    }, [dispatch]);

    const changePassword = useCallback(async (request) => {
        let {userID,oldPassword, newPassword, confirmPassword} = request;
        return new Promise(async (resolve, reject) => {
            try {
                let res = await authApi.changePassword({userID,oldPassword, newPassword, confirmPassword});
                resolve(res);

            } catch (err) {
                console.error('[Auth Api]: ', err);
                reject(new Error('Can not change Password'));
            }
        })

    }, [dispatch]);


    const signOut = useCallback(async () => {
        try {
            await authApi.signOut();
        } catch (error) {
            console.error('SignOut failed:', error);
        } finally {
            localStorage.clear();
            sessionStorage.clear();
            dispatch({ type: ActionType.SIGN_OUT });
            window.location.href = paths.index;
        }
    }, [dispatch]);

    const signOutDueToIdle = useCallback(async () => {
        // 1. แสดง Popup และรอการตอบสนองจากผู้ใช้
        await Swal.fire({
            icon: 'error',
            title: 'Session timeout',
            text: 'Please log in again.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#F04438',
            allowOutsideClick: false, // ป้องกันการปิด Popup โดยคลิกข้างนอก
            allowEscapeKey: false,    // ป้องกันการปิด Popup ด้วยปุ่ม Esc
        });

        // 2. เรียกใช้ฟังก์ชัน signOut หลักหลังจากผู้ใช้กด "ตกลง"
        await signOut();

    }, [signOut]);

    const getSessionTimeout = () => {
        try {
            return 20 * 60 * 1000;
        } catch (e) {
            console.error("error getSessionTimeout ", e);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                ...state,
                issuer: Issuer.appAuth,
                signIn,
                signOut,
                changePassword,
                getToken,
                getProfile,
                getPermission,
                signOutDueToIdle,
                getSessionTimeout
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export const AuthConsumer = AuthContext.Consumer;
